import { NoteInputComponentProps } from "design-system/organisms/chat/MessageInput"
import React, { useEffect, useRef } from "react"
import { useMentionInput } from "../lib/useMentionInput"

export function NoteTextareaInput({
  value: rawValue,
  onChange: onExternalChange,
  onKeyDown: externalOnKeyDown,
  forwardedRef,
  dropdownPlacement,
  ...rest
}: NoteInputComponentProps) {
  // Keep a stable ref to the external onChange so the hook callback doesn't become stale
  const onExternalChangeRef = useRef(onExternalChange)
  onExternalChangeRef.current = onExternalChange

  const mention = useMentionInput({
    dropdownPlacement,
    // Called by the hook whenever the raw value changes (typing or mention selection)
    onRawChange: (rawText) => {
      if (!onExternalChangeRef.current) return
      // Propagate the raw value up to the parent (ChatHistory inputs state) via a synthetic event
      const syntheticEvent = {
        target: { value: rawText },
      } as React.ChangeEvent<HTMLTextAreaElement>
      onExternalChangeRef.current(syntheticEvent)
    },
  })

  // Keep a stable ref to clear so the effect doesn't re-run on every render
  const clearRef = useRef(mention.clear)
  clearRef.current = mention.clear

  // Sync internal display state when the parent clears the value (e.g. after submit)
  useEffect(() => {
    if (!rawValue) {
      clearRef.current()
    }
  }, [rawValue])

  // Assign both the hook's internal ref and the forwarded ref to the same textarea node
  const setRefs = (node: HTMLTextAreaElement | null) => {
    ;(mention.textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
    if (typeof forwardedRef === "function") {
      forwardedRef(node)
    } else if (forwardedRef) {
      ;(forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    mention.textareaHandlers.onKeyDown(e)
    // If mention navigation didn't consume the event, let the parent handle it (e.g. Enter to submit)
    if (!e.defaultPrevented) {
      externalOnKeyDown?.(e)
    }
  }

  return (
    <>
      <textarea
        {...rest}
        ref={setRefs}
        value={mention.displayValue}
        onChange={mention.textareaHandlers.onChange}
        onKeyDown={handleKeyDown}
        onBlur={mention.textareaHandlers.onBlur}
      />
      {mention.dropdown}
    </>
  )
}
